import { ListBox, MultiSelect, Option, OptionGroup } from '@workramp/ui';
import { useMemo } from 'react';
import { contains } from 'underscore';
import { I18n } from '~/common/explicit-exports';
import { useViewportBreakpoint } from '~/hooks';
import { isMobile } from '~/utils';
import { MixPanel } from '~/common';
import { ORDER_KEYS, OrderByOptions, SORT_KEYS, SortByOptions, LearnerSearchMixpanelEvents } from '../../constants';
import { useSearch } from '../../providers';

const RECENT = I18n.t('js.learner.advanced_search.sort.recent');
const RELEVANCE = I18n.t('js.learner.advanced_search.sort.relevance');
const NEWEST_TO_OLDEST = I18n.t('js.learner.advanced_search.sort.newest_to_oldest');
const OLDEST_TO_NEWEST = I18n.t('js.learner.advanced_search.sort.oldest_to_newest');
const MOST_TO_LEAST = I18n.t('js.learner.advanced_search.sort.most_to_least');
const SORT_LABEL = I18n.t('js.learner.advanced_search.sort.sort_label');
const ORDER_LABEL = I18n.t('js.learner.advanced_search.sort.order_label');

export const AdvancedSearchSortSelect = () => {
  const { sortBy, setSortBy, sortOrder, setSortOrder } = useSearch();
  const viewportBreakpoint = useViewportBreakpoint();

  const selection: Set<string> = useMemo(() => new Set([sortBy, sortOrder]), [sortBy, sortOrder]);

  const handleSelectionChange = (s: 'all' | Set<string>) => {
    if (s === 'all') {
      // MultiOptionFilter only supports single selection, so this should never happen
      return;
    }

    // Determine newly selected key by removing the current sortBy and sortOrder keys from the new selection set
    const newSelectionSet = new Set([...s].filter((key) => key !== sortBy && key !== sortOrder));
    const newSelectedKey = newSelectionSet.values().next().value;

    if (contains(SORT_KEYS, newSelectedKey)) {
      setSortBy(newSelectedKey);

      MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_CHANGES_SORT, {
        sortOption: newSelectedKey,
      });
      // Special case for RELEVANCE sort option, since there is only one order option we need to default to it when it's selected
      if (newSelectedKey === SortByOptions.RELEVANCE) {
        setSortOrder(OrderByOptions.DESC);
      }
    }

    if (contains(ORDER_KEYS, newSelectedKey)) {
      setSortOrder(newSelectedKey);
    }
  };

  const dropdownLabel = sortBy === SortByOptions.RECENT ? RECENT : RELEVANCE;
  const buttonSize = isMobile(viewportBreakpoint) ? 'sm' : 'md';

  return (
    <MultiSelect label={dropdownLabel} title={dropdownLabel} minWidth={200} size={buttonSize}>
      <ListBox
        data-testid="advanced-search-sort-select"
        selectedKeys={selection}
        selectionMode="multiple"
        onSelectionChange={handleSelectionChange}
        aria-label={SORT_LABEL}
      >
        <OptionGroup title={SORT_LABEL.toUpperCase()}>
          <Option key={SortByOptions.RECENT}>{RECENT}</Option>
          <Option key={SortByOptions.RELEVANCE}>{RELEVANCE}</Option>
        </OptionGroup>
        <OptionGroup title={ORDER_LABEL.toUpperCase()}>
          {sortBy === SortByOptions.RECENT && <Option key={OrderByOptions.DESC}>{NEWEST_TO_OLDEST}</Option>}
          {sortBy === SortByOptions.RECENT && <Option key={OrderByOptions.ASC}>{OLDEST_TO_NEWEST}</Option>}
          {sortBy !== SortByOptions.RECENT && <Option key={OrderByOptions.DESC}>{MOST_TO_LEAST}</Option>}
        </OptionGroup>
      </ListBox>
    </MultiSelect>
  );
};
